import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { paymentErrorType } from 'features/payment/types';
import { useStoreContext } from 'features/store';

import { IServerOrder } from '@rbi-ctg/menu';
import { logCartPlaceOrder } from 'experiments/checkout-v2/treatment/pages/cart/payment/utils';
import { useGetOrderLazyQuery } from 'generated/graphql-gateway';
import { IUseCommitOrderParams } from 'hooks/commit-order/types';
import { useCommitOrder } from 'hooks/commit-order/use-commit-order';
import { useCart } from 'hooks/use-cart';
import useErrorModal from 'hooks/use-error-modal';
import { useIOSLiveActivities } from 'hooks/use-ios-live-activities';
import { useHandleCommitError } from 'pages/cart/hooks/use-handle-commit-error';
import { actions, useAppDispatch } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { OrderStatus } from 'state/order/types';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { useCheckoutContext } from '../use-checkout-context';
import { useCheckoutLoadingState } from '../use-checkout-loading-state';

import { useBuildCommitOrderPayload } from './use-build-commit-order-payload';
import { useRequestPaymentDetails } from './use-get-payment-details';
import { useLoyaltyDiscounts } from './use-loyalty-discounts';

const alreadyCommittedRegex = /already been committed|already committed/i;

export const useCheckoutSubmit = () => {
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);

  const router = useRouter();
  const dispatch = useAppDispatch();

  const { store } = useStoreContext();
  const { emptyCart } = useCart();
  const { emptyMinimalCart } = useMinimalCart();
  const enableMinimalCartUi = useMinimalCartUi();
  const { startActivity: startLiveActivity } = useIOSLiveActivities();
  const {
    totals,
    paymentValues,
    isPaymentType,
    isPaymentProcessor,
    serverOrder,
    distanceToStore,
    checkoutPaymentMethodId,
    setCheckoutState,
    wasCommitOrderCalled,
  } = useCheckoutContext();
  const { subTotalCents = 0, taxCents = 0 } = totals || {};
  const { isOrderPriceLoading } = useCheckoutLoadingState();

  const {
    data: paymentDetails,
    isLoading: isRequestingPaymentDetails,
    error: paymentDetailsError,
    requestPayment: requestPaymentDetails,
  } = useRequestPaymentDetails();

  const {
    buildCommitOrderPayloadWithAccount,
    buildCommitOrderPayloadWithoutAccount,
  } = useBuildCommitOrderPayload();

  const { rewardDiscountAmount, additionalDiscountAmount } = useLoyaltyDiscounts();
  const isOneTimeCardPayment = Boolean(
    paymentValues && !paymentValues.saveCard && paymentValues.cardNumber
  );
  const [shouldDisplayErrorDialog, setShouldDisplayErrorDialog] = useState(false);
  const setWasCommitOrderCalled = useCallback(
    (value: boolean) => {
      setCheckoutState(prev => ({
        ...prev,
        wasCommitOrderCalled: value,
      }));
    },
    [setCheckoutState]
  );

  const commitOrderParams: IUseCommitOrderParams = useMemo(
    () => ({
      rbiOrderId: serverOrder && serverOrder.rbiOrderId,
      distanceToStore,
      checkoutData: {
        savings: additionalDiscountAmount,
        rewardsSaving: rewardDiscountAmount,
        tax: taxCents,
        total: subTotalCents + taxCents,
        subtotal: subTotalCents,
      },
    }),
    [
      serverOrder,
      distanceToStore,
      additionalDiscountAmount,
      rewardDiscountAmount,
      taxCents,
      subTotalCents,
    ]
  );

  const {
    commitOrder,
    forceCommitOrder,
    // TODO: The commit error result and the commit order failure should be unified and returned as a single result.
    // https://rbictg.atlassian.net/browse/BKPE-22766
    commitResult: { error: commitError },
    loading: isCommitOrderLoading,
    success: commitOrderSuccess,
    failure: commitOrderFailure,
  } = useCommitOrder(commitOrderParams);

  const commitOrderError = commitError || commitOrderFailure;
  const [fetchOrder] = useGetOrderLazyQuery({ fetchPolicy: 'network-only' });
  const isAlreadyCommittedCommitError = useMemo(() => {
    if (!commitError) {
      return false;
    }

    if (alreadyCommittedRegex.test(commitError.message)) {
      return true;
    }

    return commitError.graphQLErrors?.some(error => alreadyCommittedRegex.test(error.message));
  }, [commitError]);

  const shouldCommitOrder = [
    OrderInjectionFlowVariations.INJECT_NOW,
    OrderInjectionFlowVariations.INJECT_AND_HOLD,
    OrderInjectionFlowVariations.TIMED_FIRE,
  ].includes(orderInjectFlow as OrderInjectionFlowVariations);

  const logContext = useMemo(
    () => ({
      isOneTimeCardPayment,
      isPaymentType,
      ...commitOrderParams,
    }),
    [isOneTimeCardPayment, isPaymentType, commitOrderParams]
  );

  const navigateToOrderConfirmation = useCallback(
    (completedOrder: IServerOrder) => {
      // Prevents going back to the checkout screen.
      router.dismissTo(routes.base);

      router.push(`${routes.orderConfirmation}/${completedOrder.rbiOrderId}?showToast=true`);
    },
    [router]
  );

  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Submit Order Error',
  });

  const handleRequestPaymentDetailsError = useCallback(() => {
    if (!shouldDisplayErrorDialog) {
      return;
    }

    logger.error({
      message: '[useCheckoutSubmit][submitOrder]: Error requesting payment details',
      paymentDetailsError,
      ...logContext,
    });
    let message = '';
    switch (paymentDetailsError) {
      case paymentErrorType.applePayGeneric:
        message = t('orderFailureMessageForApplePay');
        break;
      case paymentErrorType.googlePayGeneric:
        message = t('orderFailureMessageForGooglePay');
        break;
      case paymentErrorType.cashAppGeneric:
        message = t('cashAppError');
        break;
      case paymentErrorType.cashAppDeclined:
        message = t('cashAppDecline');
        break;
      default:
        message = t('pspPaymentDeclinedTransactionForterTitle');
    }
    openErrorDialog({ message });
    setShouldDisplayErrorDialog(false);
  }, [logContext, openErrorDialog, paymentDetailsError, shouldDisplayErrorDialog]);

  useHandleCommitError({
    fdAccountId: checkoutPaymentMethodId ?? '',
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm: forceCommitOrder,
    commitError: isAlreadyCommittedCommitError ? undefined : commitError,
  });

  const handleAlreadyCommittedOrder = useCallback(async () => {
    const orderId = serverOrder?.rbiOrderId;
    if (!orderId) {
      logger.warn({
        message: '[useCheckoutSubmit][handleAlreadyCommittedOrder]: Missing order id',
        commitErrorMessage: commitError?.message,
        ...logContext,
      });
      return;
    }

    try {
      const { data, error } = await fetchOrder({ variables: { id: orderId } });
      if (error) {
        throw error;
      }

      const committedOrder = data?.order ? ((data.order as unknown) as IServerOrder) : null;
      const isOrderCommitted =
        committedOrder?.status === OrderStatus.INSERT_SUCCESSFUL ||
        committedOrder?.status === OrderStatus.UPDATE_SUCCESSFUL;

      if (!committedOrder || !isOrderCommitted) {
        logger.warn({
          message:
            '[useCheckoutSubmit][handleAlreadyCommittedOrder]: Order not committed after retry',
          orderId,
          status: committedOrder?.status ?? null,
          ...logContext,
        });
        openErrorDialog({ message: t('orderFailureMessage') });
        return;
      }

      setCheckoutState(prev => ({ ...prev, serverOrder: committedOrder }));

      if (enableMinimalCartUi) {
        emptyMinimalCart();
      } else {
        emptyCart();
      }
      startLiveActivity(committedOrder);
      navigateToOrderConfirmation(committedOrder);
    } catch (error) {
      logger.error({
        error,
        message: '[useCheckoutSubmit][handleAlreadyCommittedOrder]: Error fetching order',
        orderId,
        ...logContext,
      });
      openErrorDialog({ message: t('orderFailureMessage') });
    }
  }, [
    serverOrder?.rbiOrderId,
    commitError,
    logContext,
    fetchOrder,
    openErrorDialog,
    setCheckoutState,
    emptyCart,
    emptyMinimalCart,
    enableMinimalCartUi,
    startLiveActivity,
    navigateToOrderConfirmation,
  ]);

  useEffect(() => {
    if (!isAlreadyCommittedCommitError || commitOrderSuccess) {
      return;
    }

    handleAlreadyCommittedOrder();
  }, [isAlreadyCommittedCommitError, commitOrderSuccess, handleAlreadyCommittedOrder]);

  // Handles the loading state of the order submission
  useEffect(() => {
    const isCurrentlySubmitting =
      isRequestingPaymentDetails ||
      isCommitOrderLoading ||
      (wasCommitOrderCalled && !commitOrderSuccess && !commitOrderError) ||
      // Keep submitting state true during success to prevent early redirects
      commitOrderSuccess;

    setCheckoutState(prev => ({
      ...prev,
      isSubmittingOrder: isCurrentlySubmitting,
    }));
  }, [
    isRequestingPaymentDetails,
    isCommitOrderLoading,
    wasCommitOrderCalled,
    commitOrderSuccess,
    setCheckoutState,
    commitOrderError,
  ]);

  // Handles order submission
  useEffect(() => {
    if (isOrderPriceLoading || !serverOrder) {
      return;
    }
    if (commitOrderSuccess) {
      if (enableMinimalCartUi) {
        emptyMinimalCart();
      } else {
        emptyCart();
      }
      // Start IOS Live Activity Widget
      startLiveActivity(serverOrder);

      navigateToOrderConfirmation(serverOrder);
    }

    if (commitOrderError) {
      if (serverOrder?.orderErrors && !commitError && wasCommitOrderCalled) {
        setWasCommitOrderCalled(false);
        openErrorDialog({ message: t('orderFailureMessage') });
        return;
      }
      setWasCommitOrderCalled(false);
    }
  }, [
    store.number,
    serverOrder,
    isOrderPriceLoading,
    commitOrderSuccess,
    commitOrderError,
    emptyCart,
    emptyMinimalCart,
    enableMinimalCartUi,
    startLiveActivity,
    navigateToOrderConfirmation,
    commitError,
    openErrorDialog,
    wasCommitOrderCalled,
    setWasCommitOrderCalled,
  ]);

  // Handles the trigger of the commit order when the payment details are available
  useEffect(() => {
    if (isRequestingPaymentDetails || wasCommitOrderCalled || commitOrderError) {
      return;
    }
    async function handleCommitOrder() {
      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: true,
      }));

      try {
        const payload = await buildCommitOrderPayloadWithoutAccount(paymentDetails);
        if (!payload) {
          logger.error({
            message:
              '[useCheckoutSubmit][submitOrder]: Empty order payload from buildCommitOrderPayloadWithoutAccount',
            ...logContext,
          });
          setCheckoutState(prev => ({
            ...prev,
            isBuildingCommitOrderPayload: false,
          }));
          return;
        }
        commitOrder(payload);
      } catch (error) {
        // Handle errors from reload or payload building
        // This ensures loading states are cleared when errors occur
        logger.error({
          error,
          message: '[useCheckoutSubmit][handleCommitOrder]: Error building commit order payload',
          ...logContext,
        });
        setCheckoutState(prev => ({
          ...prev,
          isBuildingCommitOrderPayload: false,
        }));
        // Re-throw so error handling can display the error modal
        throw error;
      } finally {
        setCheckoutState(prev => ({
          ...prev,
          isBuildingCommitOrderPayload: false,
        }));
      }
    }
    if (!paymentDetailsError && paymentDetails) {
      setWasCommitOrderCalled(true);
      handleCommitOrder();
    }

    if (paymentDetailsError) {
      handleRequestPaymentDetailsError();
    }
  }, [
    paymentDetails,
    isRequestingPaymentDetails,
    buildCommitOrderPayloadWithoutAccount,
    commitOrder,
    paymentDetailsError,
    logContext,
    handleRequestPaymentDetailsError,
    isCommitOrderLoading,
    commitOrderSuccess,
    commitError,
    wasCommitOrderCalled,
    setWasCommitOrderCalled,
    setCheckoutState,
    commitOrderError,
  ]);

  const submitOrder = async () => {
    setShouldDisplayErrorDialog(true);
    if (!shouldCommitOrder) {
      logger.warn({
        message: '[useCheckoutSubmit][submitOrder]: Skipping order commit',
        shouldCommitOrder,
        ...logContext,
      });
      return;
    }
    logCartPlaceOrder();
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    if (
      isOneTimeCardPayment ||
      isPaymentProcessor.braintree ||
      isPaymentType.cashApp ||
      isPaymentType.googlePay ||
      isPaymentType.applePay
    ) {
      // This method asynchronously fetches the payment details.
      // Once paymentDetails is updated, an effect (defined above) will trigger commitOrder.
      // This ensures that the order is only committed after the latest payment information is available.
      requestPaymentDetails();
    } else {
      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: true,
      }));
      try {
        const payload = await buildCommitOrderPayloadWithAccount();
        if (!payload) {
          logger.error({
            message:
              '[useCheckoutSubmit][submitOrder]: Empty order payload from buildCommitOrderPayloadWithAccount',
            ...logContext,
          });
          return;
        }

        setWasCommitOrderCalled(true);
        commitOrder(payload);
      } catch (error) {
        // Handle errors from reload or payload building
        // This ensures loading states are cleared when errors occur
        logger.error({
          error,
          message:
            '[useCheckoutSubmit][submitOrder]: Error building commit order payload with account',
          ...logContext,
        });
        // Re-throw so error handling can display the error modal
        throw error;
      } finally {
        setCheckoutState(prev => ({
          ...prev,
          isBuildingCommitOrderPayload: false,
        }));
      }
    }
  };

  return {
    navigateToOrderConfirmation,
    submitOrder,
    isCommitOrderLoading,
    ErrorDialog,
    wasCommitOrderCalled,
    commitOrderSuccess,
    commitOrderError,
  };
};
